import React, { useState, useEffect, useCallback, useMemo } from 'react';
import KernelCapsLockWarning from './KernelCapsLockWarning';
import { getTotpStatus, startTotpSetup, enableTotp, disableTotp, regenerateRecoveryCodes, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import qrcode from '../utils/kernelQrcode';
import './kernelTotp.css';

/** Codice QR (SVG) di un indirizzo otpauth: si scansiona con l'app di autenticazione. */
export function Qr({ text }) {
  const svg = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  }, [text]);
  return <div className="totp-qr" role="img" aria-label="QR" dangerouslySetInnerHTML={{ __html: svg }} />;
}

export const group = (secret) => (secret || '').replace(/(.{4})/g, '$1 ').trim();

/**
 * Autenticazione a due fattori con app (Google Authenticator, Microsoft Authenticator, 1Password...), nel Profilo.
 * Flusso: password → codice QR (o segreto da digitare) → primo codice → codici di recupero (si vedono una sola volta).
 * Per disattivarla o rigenerare i codici di recupero servono la password e un codice.
 */
function KernelTotpSection({ onChanged }) {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState('idle'); // idle | password | scan | codes | disable | recovery
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState(null);
  const [codes, setCodes] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await getTotpStatus());
    } catch (err) {
      setStatus({ enabled: false, available: false });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setStep('idle');
    setPassword('');
    setCode('');
    setSetup(null);
    setError('');
  };

  const run = async (fn) => {
    setError('');
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err, t('totp.error')));
    } finally {
      setBusy(false);
    }
  };

  const startSetup = (e) => {
    e.preventDefault();
    run(async () => {
      setSetup(await startTotpSetup(password));
      setPassword('');
      setStep('scan');
    });
  };

  const confirmEnable = (e) => {
    e.preventDefault();
    run(async () => {
      const res = await enableTotp(code.trim());
      setCodes(res.recovery_codes || []);
      setCode('');
      setSetup(null);
      setStep('codes');
      await load();
      if (onChanged) onChanged(true);
    });
  };

  const confirmDisable = (e) => {
    e.preventDefault();
    run(async () => {
      await disableTotp(password, code.trim());
      reset();
      await load();
      if (onChanged) onChanged(false);
    });
  };

  const confirmRecovery = (e) => {
    e.preventDefault();
    run(async () => {
      const res = await regenerateRecoveryCodes(password, code.trim());
      setCodes(res.recovery_codes || []);
      setPassword('');
      setCode('');
      setStep('codes');
      await load();
    });
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      setError(t('totp.copyError'));
    }
  };

  const download = () => {
    const blob = new Blob([`${t('totp.recoveryFileTitle')}\n\n${codes.join('\n')}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'codici-di-recupero.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!status) return null;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <h3>{t('totp.title')}</h3>
      </div>
      <div className="card-body">
        <p className="page-subtitle" style={{ marginTop: 0 }}>{t('totp.intro')}</p>
        {error && <div className="error-message">{error}</div>}
        {status.available === false && <div className="warning-message">{t('totp.unavailable')}</div>}

        {status.available !== false && step === 'idle' && (
          <>
            <p>
              <span className={`badge ${status.enabled ? 'badge-success' : 'badge-warning'}`}>{status.enabled ? t('totp.statusOn') : t('totp.statusOff')}</span>
              {status.enabled && <span style={{ opacity: 0.7, marginLeft: 10 }}>{t('totp.recoveryLeft', { count: status.recovery_left })}</span>}
            </p>
            {status.enabled && status.company_enabled && <p className="page-subtitle" style={{ marginTop: 0 }}>{t('totp.requiredByCompany')}</p>}
            <div className="form-actions">
              {status.enabled ? (
                <>
                  <button type="button" className="btn btn-secondary" onClick={() => setStep('recovery')}>{t('totp.newRecovery')}</button>
                  {!status.company_enabled && <button type="button" className="btn btn-secondary" onClick={() => setStep('disable')}>{t('totp.disable')}</button>}
                </>
              ) : (
                <button type="button" className="btn" onClick={() => setStep('password')}>{t('totp.enable')}</button>
              )}
            </div>
          </>
        )}

        {step === 'password' && (
          <form onSubmit={startSetup}>
            <p>{t('totp.askPassword')}</p>
            <div className="form-group" style={{ maxWidth: 360 }}>
              <label htmlFor="totp-pw">{t('totp.password')}</label>
              <input id="totp-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required autoFocus />
              <KernelCapsLockWarning />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn" disabled={busy}>{t('totp.next')}</button>
              <button type="button" className="btn btn-secondary" onClick={reset}>{t('common.cancel')}</button>
            </div>
          </form>
        )}

        {step === 'scan' && setup && (
          <form onSubmit={confirmEnable}>
            <ol className="totp-steps">
              <li>{t('totp.step1')}</li>
              <li>
                {t('totp.step2')}
                <div className="totp-scan">
                  <Qr text={setup.uri} />
                  <div>
                    <p style={{ margin: '0 0 6px' }}>{t('totp.orSecret')}</p>
                    <code className="totp-secret">{group(setup.secret)}</code>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(setup.secret)}>{copied ? t('totp.copied') : t('totp.copySecret')}</button>
                      <a className="btn btn-secondary btn-sm" href={setup.uri}>{t('totp.openApp')}</a>
                    </div>
                  </div>
                </div>
              </li>
              <li>{t('totp.step3')}</li>
            </ol>
            <div className="form-group" style={{ maxWidth: 220 }}>
              <label htmlFor="totp-code">{t('totp.code')}</label>
              <input id="totp-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} inputMode="numeric" maxLength={6} placeholder="000000" autoComplete="one-time-code" required />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn" disabled={busy || code.length !== 6}>{t('totp.activate')}</button>
              <button type="button" className="btn btn-secondary" onClick={reset}>{t('common.cancel')}</button>
            </div>
          </form>
        )}

        {step === 'codes' && (
          <>
            <div className="success-message">{t('totp.codesTitle')}</div>
            <p>{t('totp.codesHint')}</p>
            <div className="totp-codes">{codes.map((c) => <code key={c}>{c}</code>)}</div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => copy(codes.join('\n'))}>{copied ? t('totp.copied') : t('totp.copyCodes')}</button>
              <button type="button" className="btn btn-secondary" onClick={download}>{t('totp.download')}</button>
              <button type="button" className="btn" onClick={reset}>{t('totp.done')}</button>
            </div>
          </>
        )}

        {(step === 'disable' || step === 'recovery') && (
          <form onSubmit={step === 'disable' ? confirmDisable : confirmRecovery}>
            <p>{step === 'disable' ? t('totp.disableHint') : t('totp.recoveryHint')}</p>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="totp-pw2">{t('totp.password')}</label>
                <input id="totp-pw2" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required autoFocus />
                <KernelCapsLockWarning />
              </div>
              <div className="form-group">
                <label htmlFor="totp-code2">{t('totp.codeOrRecovery')}</label>
                <input id="totp-code2" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" required />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn" disabled={busy}>{step === 'disable' ? t('totp.disable') : t('totp.newRecovery')}</button>
              <button type="button" className="btn btn-secondary" onClick={reset}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default KernelTotpSection;
